// We need to explicitly import regenerator-runtime here because @classdojo/web@8.0.0
// is bundling saga related code incorrectly, not importing the runtime. This causes
// an issue when building the Next app for CI (using the `node ci-build2.js` command).
// After fixing the problem in @classdojo/web (and upgrading), we should be able to remove
// this import.
// eslint-disable-next-line import/no-extraneous-dependencies
import "regenerator-runtime/runtime";

import "extended-proptypes/lib/extend-from-standalone";
// eslint-disable-next-line @web-monorepo/no-css-file-imports
import "@classdojo/web/pods/font/dojoText.css";
import { initialize } from "@classdojo/web";
import { NessieThemeProvider } from "@classdojo/web/nessie";
import { ERROR_CATCH_TYPE, ErrorHandler } from "@classdojo/web/pods/errorHandling";
import AppTopLevelErrorBoundary from "@classdojo/web/pods/errorHandling/components/AppTopLevelErrorBoundary";
import ErrorBanner from "@classdojo/web/pods/errorHandling/components/ErrorBanner";
import { makeBackwardsCompatibleTranslate } from "@classdojo/web/pods/i18n";
import { UserActionsHistory } from "@classdojo/web/pods/userActionsHistory";
import { PodInstallFunction } from "@web-monorepo/shared/podInfra";
import i18n from "i18next";
import Backend from "i18next-http-backend";
import BackendAdapter from "i18next-multiload-backend-adapter";
import type { MultiloadBackendOptions } from "i18next-multiload-backend-adapter";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { initReactI18next } from "react-i18next";
import { Provider } from "react-redux";
import { useSelector, useDispatch } from "react-redux";
import { compose, applyMiddleware, createStore, combineReducers } from "redux";
import type { Store, Reducer } from "redux";
import createSagaMiddleware, { Saga, Task } from "redux-saga";
import { all, call } from "redux-saga/effects";
import { userSelector } from "pods/auth";
import { logEvent } from "pods/logs";
import pods from "src/pods";
import { createWebErrorHandler, ignorableToolkitErrors } from "src/toolkitErrorHandling";
import AppRouter from "./AppRouter";
import * as Telemetry from "@web-monorepo/telemetry";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";

if (Config.nodeEnv === "production") {
  Telemetry.init({
    dsn: "https://e55ac9522b26046ee9c90f4d3cc0b5e2@sentry.classdojo.com/12",
    allowUrls: [/https:\/\/([\w-]+\.)*classdojo\.(com|test)(:\d+)?\/(.*\/)?[^\/]+\.js/],
    tracing: {
      reactRouter: {
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      },
    },
  });
}

// Functions used by native iOS. Ensure backwards compatibility
function registerGlobalDispatcher(store: Store) {
  global.dispatchAction = function (action: string) {
    store.dispatch(JSON.parse(action) as any);
  };

  if (global.dispatchActionsOnLoad && global.dispatchActionsOnLoad.length) {
    global.dispatchActionsOnLoad.forEach(function (action) {
      global.dispatchAction(action);
    });
    global.dispatchActionsOnLoad = [];
  }
}

const composeEnhancers =
  global.window && global.window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? global.window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    : compose;

const makeStore = (errorHandler: ErrorHandler, userActionsHistory: UserActionsHistory) => {
  const reducerMap: Record<string, Reducer> = {};
  function installReducer(path: string, reducer: Reducer) {
    if (Object.prototype.hasOwnProperty.call(reducerMap, path) && reducerMap[path] !== reducer) {
      throw new Error(`Reducer already registered at path: ${path}`);
    }
    reducerMap[path] = reducer;
  }

  const sagaList: Saga[] = [];
  function installSaga(saga: Saga) {
    if (!sagaList.includes(saga)) {
      sagaList.push(saga);
    }
  }
  const sagaMiddleware = createSagaMiddleware({});

  const middlewareList = [errorHandler.reduxMiddleware, sagaMiddleware, userActionsHistory.reduxMiddleware];
  function installPod(installer: PodInstallFunction) {
    if (!installer) return;
    installer(installReducer, installSaga, installPod);
  }

  pods.forEach(installPod);

  const store: Store & {
    sagaTask?: Task;
  } = createStore(combineReducers(reducerMap), {}, composeEnhancers(applyMiddleware(...middlewareList)));

  function* rootSaga() {
    yield all(sagaList.map((saga) => call(saga)));
  }

  store.sagaTask = sagaMiddleware.run(rootSaga);
  store.sagaTask.toPromise().catch((e) => {
    errorHandler.handleError(e, { catchType: ERROR_CATCH_TYPE.SAGA_TASK });
  });

  registerGlobalDispatcher(store);

  return store;
};

function App() {
  const { store, errorHandler, initialUrl } = useMemo(() => {
    // Toolkit is initialized with a dynamic path including teacher/class ID.
    // When an error occurs, we want to have a reference to where the app
    // was initialized.
    const initialUrl = window.location.href;

    const userActionsHistory = new UserActionsHistory();
    // Register browser history changes in our 'user actions module'
    // We are calling this code 'early' and only once. To ensure we track any navigation or url change that happens
    // even before we mount the top component in the app.
    userActionsHistory.listenToBrowserUrlChanges();

    const errorHandler = createWebErrorHandler(userActionsHistory);
    const store = makeStore(errorHandler, userActionsHistory);

    return { store, errorHandler, initialUrl };
  }, []);

  return (
    <div>
      <Provider store={store}>
        <I18nSetup>
          <NessieThemeProvider>
            <ErrorBanner initialUrl={initialUrl} />
            <AppTopLevelErrorBoundary errorHandler={errorHandler} excludeErrors={ignorableToolkitErrors}>
              <AppRouter />
            </AppTopLevelErrorBoundary>
          </NessieThemeProvider>
        </I18nSetup>
      </Provider>
    </div>
  );
}

export default App;

function I18nSetup({ children }: { children: JSX.Element }) {
  const dispatch = useDispatch();
  const [didInit, setDidInit] = useState(false);

  initialize({
    translate: makeBackwardsCompatibleTranslate(i18n),
    logEvent: (...args) => dispatch(logEvent(...args)),
  });

  const user = useSelector(userSelector);
  const locale = user?.locale;
  useLayoutEffect(() => {
    if (locale) {
      if (didInit) {
        i18n.changeLanguage(locale).then(() => {
          dispatch({ type: "internal/interactable" });
        });
      } else {
        i18n
          .use(BackendAdapter)
          // pass the i18n instance to react-i18next.
          .use(initReactI18next)
          // for all options read: https://www.i18next.com/overview/configuration-options
          .init<MultiloadBackendOptions>({
            lng: locale,
            fallbackLng: {
              en: ["en-US"],
              "es-MX": ["es-ES"],
              "es-ES": ["es-MX"],
              "fr-CA": ["fr-FR"],
              "fr-FR": ["fr-CA"],
              "zh-CN": ["zh-HK", "zh-TW"],
              "zh-TW": ["zh-HK", "zh-CN"],
              "zh-HK": ["zh-TW", "zh-CN"],
              default: ["en"],
            },

            // debug: true,

            backend: {
              backend: new Backend(),
              backendOption: {
                allowMultiLoading: true,
                crossDomain: true,
                loadPath: `/locales/resources.json?lng=__lng__&ns=__ns__`,
              },
            },

            interpolation: {
              prefix: "__",
              suffix: "__",
              escapeValue: false, // not needed for react as it escapes by default
            },

            react: {
              useSuspense: false,
            },
          })
          .then(() => {
            dispatch({ type: "internal/interactable" });
          });
        setDidInit(true);
      }
    }
    // NOTE: don't want effect to refire if `didInit` changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, dispatch, setDidInit]);

  if (!didInit) {
    return null;
  }

  return children;
}
